import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllInstagramCompareFeedPosts.graphql';
import { Query, QueryallInstagramCompareFeedPostsArgs } from '../../__generated__/globalTypes';

export const useAllInstagramCompareFeedPostsQuery = (
  options?: QueryHookOptions<Query, QueryallInstagramCompareFeedPostsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallInstagramCompareFeedPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllInstagramCompareFeedPostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallInstagramCompareFeedPostsArgs>
): QueryResult<Query, QueryallInstagramCompareFeedPostsArgs> & {
  getAllInstagramCompareFeedPosts: LazyQueryExecFunction<Query, QueryallInstagramCompareFeedPostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllInstagramCompareFeedPosts, result] = useLazyQuery<Query, QueryallInstagramCompareFeedPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllInstagramCompareFeedPosts, ...result };
};

export const useAllInstagramCompareFeedPostsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllInstagramCompareFeedPosts = useCallback(
    (options?: Omit<QueryOptions<QueryallInstagramCompareFeedPostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallInstagramCompareFeedPostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllInstagramCompareFeedPosts };
};
// Suspense Query Hooks
export const useAllInstagramCompareFeedPostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallInstagramCompareFeedPostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallInstagramCompareFeedPostsArgs>(QUERY, {
    ...options
  });

  return result;
};
